import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/shared/libs';
import {
  Dialog,
  DialogFooter,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  LoaderOverlay,
} from '@/shared/ui';
import {
  DEAL_SOURCE_I18N_KEY,
  DEAL_STATUS_BADGE_CLASS,
  DEAL_STATUS_I18N_KEY,
  useDeal,
  useDealEvents,
} from '@entities/Deal';

import { DealEventLog } from './DealEventLog';
import { DealItems } from './DealItems';
import { DealTicketActions } from './DealTicketActions';

export interface DealTicketProps {
  dealId: string;
  onClose: () => void;
}

export const DealTicket: FC<DealTicketProps> = ({ dealId, onClose }) => {
  const { i18n, t } = useTranslation();
  const dealQuery = useDeal(dealId);
  const eventsQuery = useDealEvents(dealId, { limit: 100, page: 1 });
  const deal = dealQuery.data?.deal;

  return (
    <Dialog
      className="overflow-hidden"
      title={t('pages.admin.deals.ticket.title')}
      width={780}
      onClose={onClose}>
      {dealQuery.isLoading && !deal && <LoaderOverlay show />}

      {dealQuery.isError && (
        <Empty>
          <EmptyHeader>
            <EmptyTitle>{t('errors.deals.loadFailed')}</EmptyTitle>
            <EmptyDescription>{t('errors.unknown')}</EmptyDescription>
          </EmptyHeader>
        </Empty>
      )}

      {!!deal && (
        <>
          <div className="max-h-[min(70dvh,calc(90dvh-8rem))] space-y-5 overflow-y-auto overscroll-contain pr-1">
            <div className="rounded-xl border border-border bg-muted/30 p-4">
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={cn(
                    'rounded-full px-2 py-0.5 text-[10px] font-medium',
                    DEAL_STATUS_BADGE_CLASS[deal.status],
                  )}>
                  {t(DEAL_STATUS_I18N_KEY[deal.status])}
                </span>
                <span className="text-xs text-muted-foreground">
                  {t(DEAL_SOURCE_I18N_KEY[deal.source])}
                </span>
              </div>
              <dl className="mt-3 grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
                <div>
                  <dt className="text-xs text-muted-foreground">
                    {t('pages.admin.deals.columns.merchant')}
                  </dt>
                  <dd className="font-medium">
                    {deal.merchant_brand ?? deal.merchant_id ?? '—'}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">
                    {t('pages.admin.deals.ticket.country')}
                  </dt>
                  <dd>{deal.merchant_country}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">
                    {t('pages.admin.deals.ticket.currency')}
                  </dt>
                  <dd>{deal.merchant_currency}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">ID</dt>
                  <dd className="truncate font-mono text-xs">{deal.id}</dd>
                </div>
              </dl>
            </div>

            <section>
              <h2 className="mb-2 text-sm font-semibold">
                {t('pages.admin.deals.ticket.items')}
              </h2>
              <DealItems items={deal.items} />
            </section>

            <section>
              <h2 className="mb-2 text-sm font-semibold">
                {t('pages.admin.deals.ticket.actionLog')}
              </h2>
              {eventsQuery.isLoading ? (
                <p className="text-sm text-muted-foreground">
                  {t('pages.admin.deals.ticket.loadingEvents')}
                </p>
              ) : (
                <DealEventLog
                  events={eventsQuery.data?.items ?? []}
                  language={i18n.language}
                />
              )}
            </section>
          </div>

          <DialogFooter endSlot={<DealTicketActions deal={deal} />} />
        </>
      )}
    </Dialog>
  );
};

DealTicket.displayName = 'DealTicket';
